import type { FrameCachePort } from '../../domain/ports/FrameCachePort';
import type { FrameRepository } from '../../domain/ports/FrameRepository';
import type { Frame } from '../../domain/entities/Frame';
import type { CacheMetrics } from '../../domain/value-objects/CacheMetrics';
import type { CacheEntry } from '../../domain/value-objects/CacheEntry';
import { shouldEvict, getEvictionCandidate } from '../../domain/services/CachePolicy';

import type { RawFrameData } from '../../domain/ports/FrameRepository';

export interface FrameParser {
  (raw: RawFrameData): Frame;
}

export interface ColorBufferBuilder {
  (positions: Float32Array, count: number): Float32Array;
}

const BYTES_PER_MB = 1024 * 1024;

export class InMemoryFrameCache implements FrameCachePort {
  private readonly entries = new Map<number, CacheEntry>();
  private currentBytes = 0;
  private evictionEnabled = true;

  constructor(
    private readonly limitMb: number,
    private readonly repository: FrameRepository,
    private readonly parser: FrameParser,
    private readonly buildColors: ColorBufferBuilder,
    private readonly debug = false,
  ) {}

  setEvictionEnabled(enabled: boolean): void {
    this.evictionEnabled = enabled;
  }

  async loadFrame(frameId: number, protectedFrames?: Set<number>): Promise<Frame> {
    const cached = this.getFrame(frameId);
    if (cached) return cached;

    const raw = await this.repository.fetchFrame(frameId);
    const frame = this.parser(raw);
    const colorBuffer = this.buildColors(frame.pointCloud.positions, frame.pointCloud.count);

    this.putFrame(frameId, frame, colorBuffer, protectedFrames);
    return frame;
  }

  getFrame(frameId: number): Frame | null {
    const entry = this.entries.get(frameId);
    if (!entry || entry.status !== 'ready') return null;
    entry.lastAccess = Date.now();
    return entry.frame;
  }

  putFrame(frameId: number, frame: Frame, colorBuffer: Float32Array, protectedFrames?: Set<number>): void {
    const byteSize = frame.getByteSize() + colorBuffer.byteLength;
    const newSizeMb = byteSize / BYTES_PER_MB;

    // Subtract old bytes if overwriting an existing entry (concurrent load race)
    const existing = this.entries.get(frameId);
    if (existing) {
      this.currentBytes -= existing.byteSize;
    }

    if (shouldEvict(this.getMetrics(), newSizeMb)) {
      if (!this.evictionEnabled) {
        // Restore old bytes if we had an existing entry we subtracted
        if (existing) {
          this.currentBytes += existing.byteSize;
        }
        if (this.debug) {
          console.table({
            event: 'frame_skipped_no_eviction',
            frameId,
            byteSizeMb: newSizeMb.toFixed(2),
            cacheUsageMb: (this.currentBytes / BYTES_PER_MB).toFixed(2),
          });
        }
        return;
      }

      const evictionProtected = new Set(protectedFrames ?? []);
      evictionProtected.add(frameId);
      while (shouldEvict(this.getMetrics(), newSizeMb)) {
        const candidate = getEvictionCandidate(this.entries, evictionProtected);
        if (candidate === null) break;
        this.evict(candidate);
      }
    }

    this.entries.set(frameId, {
      frame,
      colorBuffer,
      byteSize,
      status: 'ready',
      lastAccess: Date.now(),
    });
    this.currentBytes += byteSize;

    if (this.debug) {
      console.table({
        event: 'frame_loaded',
        frameId,
        byteSizeMb: newSizeMb.toFixed(2),
        cacheUsageMb: (this.currentBytes / BYTES_PER_MB).toFixed(2),
        cacheLimit: this.limitMb,
        loadedFrames: this.entries.size,
      });
    }
  }

  evict(frameId: number): void {
    const entry = this.entries.get(frameId);
    if (!entry) return;
    const freedMb = entry.byteSize / BYTES_PER_MB;
    this.currentBytes -= entry.byteSize;
    this.entries.delete(frameId);

    if (this.debug) {
      console.table({
        event: 'frame_evicted',
        frameId,
        freedMb: freedMb.toFixed(2),
        cacheUsageMb: (this.currentBytes / BYTES_PER_MB).toFixed(2),
        loadedFrames: this.entries.size,
      });
    }
  }

  getMetrics(): CacheMetrics {
    return {
      currentMb: this.currentBytes / BYTES_PER_MB,
      limitMb: this.limitMb,
      entryCount: this.entries.size,
      loadedFrames: Array.from(this.entries.keys()),
    };
  }

  getLoadedFrameIds(): number[] {
    return Array.from(this.entries.keys());
  }

  getColorBuffer(frameId: number): Float32Array | null {
    return this.entries.get(frameId)?.colorBuffer ?? null;
  }
}
